import { useNavigate } from "react-router-dom";
import {
  Button,
  DropdownMenuItem,
  DropdownMenuSeparator,
  UserMenu as UserMenuShell,
  UserMenuHeader,
} from "@rammetec/design-system";
import { Building2, LogOut, Settings, User } from "lucide-react";
import type { AuthUser } from "@rammetec/data-access";
import { rtLogService } from "@rammetec/core";
import { useOrgLabels, useSessionContext } from "@vectorhub/application";
import { authService } from "@/authService";

export interface UserMenuProps {
  user: AuthUser;
}

function getDisplayName(user: AuthUser): string {
  const name = (user as Record<string, unknown>).displayName;
  if (typeof name === "string" && name.trim()) return name.trim();
  // Fall back to the part before @ in the email
  if (user.email) {
    const local = user.email.split("@")[0];
    if (local) return local;
  }
  return "Usuário";
}

function getInitials(name: string): string {
  const parts = name.split(/[\s@._-]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "?";
  const second = parts[1]?.[0] ?? "";
  return (first + second).toUpperCase();
}

function getUserPhotoUrl(user: AuthUser): string | undefined {
  const u = user as Record<string, unknown>;
  const raw = u.photoURL ?? u.photoUrl;
  return typeof raw === "string" && raw.trim() !== "" ? raw : undefined;
}

export function UserMenu({ user }: UserMenuProps) {
  const navigate = useNavigate();
  const { currentOrgId, availableOrgIds } = useSessionContext();
  const orgLabels = useOrgLabels(availableOrgIds);

  const photoUrl = getUserPhotoUrl(user);
  const displayName = getDisplayName(user);

  const currentOrg = currentOrgId ? orgLabels.get(currentOrgId) : undefined;
  const orgLabel =
    currentOrg?.name !== currentOrgId
      ? currentOrg?.name
      : (currentOrg?.abbreviation ?? currentOrgId);

  const handleLogout = () => {
    authService.logout().catch((err) => rtLogService.warn(err));
    navigate("/login", { replace: true });
  };

  return (
    <UserMenuShell
      src={photoUrl}
      alt={displayName ? `Foto de ${displayName}` : "Foto do usuário"}
      fallback={getInitials(displayName)}
      triggerAriaLabel="Abrir menu do usuário"
      header={
        <UserMenuHeader
          icon={<User />}
          primary={displayName}
          secondary={user.email ?? user.userId}
        />
      }
    >
      {orgLabel ? (
        <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-fg-muted">
          <Building2 className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{orgLabel}</span>
        </div>
      ) : null}

      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={() => navigate("/settings")}>
        <Settings className="h-4 w-4" />
        Configurações
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={handleLogout}>
        <LogOut className="h-4 w-4" />
        Sair
      </DropdownMenuItem>
    </UserMenuShell>
  );
}

export function UserMenuFallback() {
  return (
    <Button variant="ghost" size="sm" disabled>
      <User className="h-4 w-4" />
      Carregando
    </Button>
  );
}
